import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { CourseContentPreview } from '@/components/courses/preview/CourseContentPreview';
import { useCourse } from '@/hooks/query';
import { usePrefetchAccountVariables } from '@/hooks/query/account-variables.hooks';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { FlexContainer } from '@/ui/styled-ui';
import { canPreviewContent } from '@/utils/helpers';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CoursesActionHeader } from './CoursesActionHeader';
import { CoursesTabs } from './CoursesTabs';

const StyledPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const StyledViewerArea = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

export const ContentPreviewPage: React.FC = () => {
  const { t } = useTranslation();
  const { id: courseId } = useParams();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const prefetchAccountVariables = usePrefetchAccountVariables();
  const navigate = useNavigate();
  const location = useLocation();

  const returnUrl = (location.state as { from?: string })?.from;

  // Fetch course data
  const { course, isLoading: isCourseLoading } = useCourse({
    courseId,
    staleTime: Infinity,
  });

  const canPreview = useMemo(
    () => canPreviewContent(course, currentUser),
    [course, currentUser],
  );

  useEffect(() => {
    prefetchAccountVariables(currentAccount?.id);
  }, [currentAccount?.id, prefetchAccountVariables]);

  useEffect(() => {
    if (!canPreview && course && !isCourseLoading) {
      navigate(
        returnUrl ||
          RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](course.id),
      );
    }
  }, [canPreview, course, isCourseLoading, navigate, returnUrl]);

  if (isCourseLoading || (course && !canPreview)) {
    return (
      <FlexContainer direction="column" className="h-full">
        <FlexContainer direction="column" className="mt-5 flex-1">
          <ProgressSpinner />
          <h3>{t('content.loading')}</h3>
        </FlexContainer>
      </FlexContainer>
    );
  }

  return course ? (
    <StyledPreviewContainer>
      <CoursesActionHeader course={course} returnUrl={returnUrl} />
      <CoursesTabs course={course} />
      <StyledViewerArea>
        <CourseContentPreview course={course} />
      </StyledViewerArea>
    </StyledPreviewContainer>
  ) : null;
};
